import _ from "csi/_base/underscore";
import def from "csi/def";
import Action from "csi/def/Action";
import FieldConstructor, { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import { View } from "csi/def/View";
import ViewRef from "csi/def/ViewRef";
import Widget from "csi/form/Widget";
import html from "csi/html";
import locale from "csi/locale";
import csi from "csi/main";
import reflection from "csi/reflection";
import { resolveNode, resolveNodes } from "csi/service/nodeService";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";

const PRODUCT_HIERARCHY = "ProductHierarchy";
const PRODUCT_TYPE = "ProductType";
const viewIdForProducts = {
	Food: "AllFoodHierarchies",
	FormulatedMaterial: "AllFormulatedMaterialHierarchies",
	RetailProduct: "AllRetailProductHierarchies",
};

const ProductSelectProductHierarchyForm = declare(Widget, {

	/** @property {Array<Field>} originalFields - The fields before hierarchy fields are inserted */
	originalFields: [],

	/** @property {string} productType - type of product shown in view. Example: "Food" */
	productType: null,

	/** @property {C8Node} productHierarchy - ProductHierarchy currently selected */
	productHierarchy: null,

	/** @property {boolean} productHierarchyFieldRequired - flag to check if ProductHierarchy field is required */
	productHierarchyFieldRequired: false,

	/** @property {Field} _fieldHierarchy - Field for the top level product hierarchy */
	_fieldHierarchy: null,

	/** @property {String} _labelLeafFolder - Label for the leaf level product hierarchy folder */
	_labelLeafFolder: null,

	/** @property {Array<Field>} hierarchyFields - ProductHierarchyFolder fields */
	_hierarchyFields: [],

	/** @property {Array<Field>} hierarchyParents - ProductHierarchyLevel parents */
	_hierarchyParents: [],

	/** @property {Number} currentLevel - current level */
	_currentLevel: 0,

	/**
	 * @override
	 * Insert Product Hierarchy field if it hasn't been added
	 */
	_resolveView() {
		this.originalFields = [...this.defView.m_fields];
		this._hierarchyFields = [];

		// Pass productHierarchyFieldRequired as a controller param to specify if
		// Product Hierarchy field is required
		this._fieldHierarchy = lang.mixin(new FieldConstructor(), {
			m_bRequired: this.productHierarchyFieldRequired ? true : false,
			m_label: locale.resource(PRODUCT_HIERARCHY),
			m_name: PRODUCT_HIERARCHY,
			m_onChange: "_onChangeProductHierarchy",
			m_typeControl: def.FIELD_FILTERING_SELECT,
		});
		this.insertField(this._fieldHierarchy);
		return this.inherited(arguments);
	},

	async _getProductHierarchy(): Promise<C8Node> {
		const urlHierarchy = this.defView.getFieldValue(PRODUCT_HIERARCHY);
		return await resolveNode(urlHierarchy, this.cacheView);
	},

	/**
	 * Construct the ProductHierarchyFolders' Field when the ProductHierarchy value changed
	 * @param defView 
	 * @param field 
	 * @param value 
	 */
	async _onChangeProductHierarchy(defView, field, value) {
		const productHierarchy = this.productHierarchy = await this._getProductHierarchy();

		// Remove old fields for the ProductHierarchyFolders (when dropdown value changes)
		const ProdHierarchyFieldIdx = defView.m_fields.findIndex((elem) => elem.getControlName() === PRODUCT_HIERARCHY);

		// Remove all fields below ProductHierarchy before adding folder fields
		for (let i = ProdHierarchyFieldIdx + 1; i < this.defView.m_fields.length; i++) {
			html.orphan(this.getFieldRow(this.defView.m_fields[i]));
		}

		this._fieldHierarchy = this.getField(PRODUCT_HIERARCHY);

		this.defView.m_fields = [...this.originalFields, this._fieldHierarchy];
		this._hierarchyFields = [this._fieldHierarchy];

		if (!productHierarchy) {
			return;
		}

		await this.addProductHierarchyFolders(productHierarchy, ProdHierarchyFieldIdx);
		await this.initProductHierarchyFolders();
	},

	_onChangeProductHierarchyFolder(defView, field, value) {
		if (value === csi.EMPTY_URL) {
			this.disableHierarchyFieldChildren(field);
		} else {
			this.enableNextHierarchyField(field);
		}
	},

	/**
	 * On Product Hierarchy Leaf Folder action "Move to Product Hierarchy", do not allow moving to same leaf folder
	 */
	_onValidateLeafHierarchyFolder(defView: View, field: Field, value: any): void {
		const viewParent = this.defView.m_formData?.getParent();
		if (viewParent?.$Type === "ProductHierarchyDetails" && value === viewParent?.__Parent__) {
			throw new Error(locale.resource("MoveToProductHierarchyError"));
		}
	},

	async addProductHierarchyFolders(productHierarchy, ProdHierarchyFieldIdx) {
		const folderSubtypes = reflection.getNodeSetting(productHierarchy, "ProductHierarchyFolders");
		const numFolderLevels = folderSubtypes.length;

		// Add new fields for the ProductHierarchyFolders
		const folderSubtypeNodes = await resolveNodes(folderSubtypes, this.cacheView);
		for (let i = 0; i < numFolderLevels; i++) {
			// Create field
			const folderSubtypeNode = folderSubtypeNodes[i];
			const folderSubtypeName = folderSubtypeNode.$Name;
			// Use node name if localization does not exist for folder subtype
			const folderSubtypeLocalizedName = folderSubtypeNode.LocalizedName[locale.getLocale()] ?? folderSubtypeNode.$Name;
			const prevFolderSubtypeNode = i == 0 ? null : folderSubtypeNodes[i - 1];
			const prevFieldName = prevFolderSubtypeNode ? prevFolderSubtypeNode.$Name + "_Folder" : PRODUCT_HIERARCHY;
			const fieldFolder = lang.mixin(new FieldConstructor(), {
				m_bRequired: true,
				m_dependency: prevFieldName,
				m_label: folderSubtypeLocalizedName,
				m_name: folderSubtypeName + "_Folder",
				m_onChange: "_onChangeProductHierarchyFolder",
				m_typeControl: def.FIELD_FILTERING_SELECT,
			});

			if (i == numFolderLevels - 1) {
				this._labelLeafFolder = folderSubtypeName + "_Folder";
				fieldFolder.m_onValidate = "_onValidateLeafHierarchyFolder";
			}

			// Insert, build, and disable field
			this._hierarchyFields.push(fieldFolder);
			this.insertField(fieldFolder, ProdHierarchyFieldIdx + i + 1, true); // Insert after the index of producthierarchy field
			const tr = this._createFieldDom(null, fieldFolder);
			const ctx = this.getFormContext();
			this._buildField(ctx, fieldFolder, tr);
			this.setFieldDisabled(fieldFolder, i > this._currentLevel); // Set every field after first subtype folder to disabled
		}
	},

	/**
	 * Initialize ProductHierarchyFolder field values
	 */
	async initProductHierarchyFolders() {
		this._persistFormValues = {};

		for (let i = 1; i < this._hierarchyParents.length; i++) {
			const key = this._hierarchyParents[i][1].$Name + "_Folder";
			const value = this._hierarchyParents[i][0].$URL;
			this._persistFormValues[key] = value;
		}

		// Use _setPersistFieldsValue instead of setFieldValue due to dependency between fields
		await this._setPersistFieldsValue();

		// Disable fields for folders above current node
		for (let i = 1; i < this._hierarchyParents.length; i++) {
			const label = this._hierarchyParents[i][1].$Name + "_Folder";
			this.setFieldDisabled(label, true);
		}
	},

	disableHierarchyFieldChildren(currentField) {
		for (let i = 0; i < this._hierarchyFields.length; i++) {
			if (currentField.getControlName() === this._hierarchyFields[i].getControlName()) {
				// Disable all subsequent fields from current field
				for (let j = i + 1; j < this._hierarchyFields.length; j++) {
					const disableField = this._hierarchyFields[j];
					this.setFieldDisabled(disableField, true);
				}
			}
		}
	},

	enableNextHierarchyField(currentField) {
		for (let i = 0; i < this._hierarchyFields.length; i++) {
			if (currentField.getControlName() === this._hierarchyFields[i].getControlName()) {
				// Enable the next field and disable the fields after the next field
				const enableField = this._hierarchyFields[i + 1];
				this.setFieldDisabled(enableField, false);
				for (let j = i + 2; j < this._hierarchyFields.length; j++) {
					const disableField = this._hierarchyFields[j];
					this.setFieldDisabled(disableField, true);
				}
				return;
			}
		}
	},

	/**
	 * @override
	 * Filter to remove subtypes that don't have the correct ProductType for current view
	 */
	getFieldStore(field, value) {
		if (field.m_label === locale.resource(PRODUCT_HIERARCHY)) {
			return this.getProductHierarchyStore(field);
		} else if (_.contains(this._hierarchyFields, field)) {
			return this.getProductHierarchyFolderStore(field);
		}
		return this.inherited(arguments);
	},

	getProductHierarchyStore(field: Field) {
		if (!this.productType) {
			return null;
		}

		// create relationship for ProductHierarchy dropdown
		const productTypeEnum = xml.enumCreate(PRODUCT_TYPE, this.productType);
		const q = new Query()
			.Attr("Active", xml.OP_EQUALS, true)
			.Attr("ApplicableProductTypes", xml.OP_EQUALS, productTypeEnum, "Child:Subtype/Child:Settings");

		const rel = lang.mixin(new Relationship(), {
			m_typeNode: PRODUCT_HIERARCHY,
			m_query: q,
		});
		// create view for goto dialog
		const ctx = this.getContext();
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);
		const viewRefId = viewIdForProducts[this.productType];
		store.action = lang.mixin(new Action(), {
			m_fixedQuery: q,
			m_viewRef: lang.mixin(new ViewRef(), {
				m_id: viewRefId,
				m_url: reflection.URL_HOME,
			}),
		});

		return store;
	},

	getProductHierarchyFolderStore(field: Field) {
		const q = new Query();
		const rel = lang.mixin(new Relationship(), {
			m_url: field.getControlName() === PRODUCT_HIERARCHY ? PRODUCT_HIERARCHY : this.getPrevLevelValue(field.m_name),
			m_id: "Hierarchy",
			m_typeNode: "ProductHierarchyFolder",
		});

		// create view for goto dialog
		const ctx = this.getContext();
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

		return store;
	},

	getPrevLevelValue(attrId: string) {
		for (let i = 0; i < this._hierarchyFields.length; i++) {
			if (i > 0 && this._hierarchyFields[i].getControlName() === attrId) {
				return this.getFieldValue(this._hierarchyFields[i - 1]);
			}
		}
	},
});
export = ProductSelectProductHierarchyForm;